"use client";

// ===================================================================
// Sidebar classic mode — the tree the way Notion, ClickUp and Fibery draw it.
//
// Gal's spec (bus 99682, 06.09): a toggle between all apps and platforms,
// his folders under it, an icon rail with families — and a third, "classic"
// mode like Fibery. The first two landed on 06–08.09; this is the third.
// Decided 14.09: it is a fourth view mode next to list/grid/compact, and the
// sidebar opens in it by default.
//
// What makes it "classic" is what it leaves out, measured against two Fibery
// screens on Refero (00b4e002, 6f3f855c): no filter chips, no counts, no
// scope tabs above the tree. One search row, a Favorites block, then every
// section as a space with its items indented under it. Row actions (star,
// more) appear only on hover, the active row is a soft pill, and the view
// switcher is pinned at the bottom so the tree gets all the height between.
//
// It renders data Sidebar already resolved — labels, icons, active state,
// favorites — and owns only what is local to the tree: which folders are
// open. Drag-and-drop and edit mode stay in the list view; classic is for
// reading and moving around, not rearranging.
// ===================================================================

import { useState } from "react";
import Link from "next/link";
import { ChevronRight, MoreHorizontal, Search, Star, X } from "lucide-react";
import type { Language } from "@/contexts/SettingsContext";

export interface ClassicEntry {
  key: string;
  href: string;
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  isFav: boolean;
  children?: ClassicEntry[];
}

export interface ClassicSection {
  id: string;
  label: string;
  emoji?: string;
  collapsed: boolean;
  entries: ClassicEntry[];
}

interface Props {
  sections: ClassicSection[];
  favorites: ClassicEntry[];
  language: Language;
  onRight: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onToggleSection: (id: string) => void;
  onNavigate: (key: string) => void;
  onToggleFav: (href: string, label: string) => void;
  onMore: (e: React.MouseEvent, entry: ClassicEntry) => void;
  /** Pinned under the tree — the view-mode switcher lives here. */
  footer?: React.ReactNode;
}

const TEXT = {
  search: { he: "חיפוש", en: "Search", ru: "Поиск" },
  favorites: { he: "מועדפים", en: "Favorites", ru: "Избранное" },
  spaces: { he: "מרחבים", en: "Spaces", ru: "Пространства" },
  empty: { he: "אין תוצאות", en: "No results", ru: "Нет результатов" },
  more: { he: "פעולות", en: "More", ru: "Ещё" },
  fav: { he: "מועדף", en: "Favorite", ru: "Избранное" },
} as const;

function containsActive(entry: ClassicEntry): boolean {
  return entry.isActive || (entry.children ?? []).some(containsActive);
}

export function SidebarClassicNav({
  sections,
  favorites,
  language,
  onRight,
  searchQuery,
  onSearchChange,
  onToggleSection,
  onNavigate,
  onToggleFav,
  onMore,
  footer,
}: Props) {
  const lang: "he" | "en" | "ru" = language === "he" || language === "ru" ? language : "en";
  // Only folders the reader clicked are remembered; the rest follow the page.
  const [folderChoice, setFolderChoice] = useState<Record<string, boolean>>({});
  const searching = searchQuery.trim().length > 0;

  const toggleFolder = (key: string, isOpen: boolean) =>
    setFolderChoice((prev) => ({ ...prev, [key]: !isOpen }));

  const renderRow = (entry: ClassicEntry, depth: number) => {
    const Icon = entry.icon;
    const hasChildren = (entry.children?.length ?? 0) > 0;
    // A folder that holds the page you are on opens itself until the reader
    // closes it, and every folder opens while searching — a match hidden
    // inside a closed folder is a miss.
    const isOpen = hasChildren && (searching || (folderChoice[entry.key] ?? containsActive(entry)));

    return (
      <li key={entry.key}>
        <div
          className={`group/row relative flex h-7 items-center gap-1.5 rounded-md pe-1 text-[0.8125rem] transition-colors ${
            entry.isActive
              ? "bg-[var(--bg-muted)] font-medium text-[var(--fg-default)]"
              : "text-[var(--fg-muted)] hover:bg-[var(--bg-muted)] hover:text-[var(--fg-default)]"
          }`}
          style={{ paddingInlineStart: `${6 + depth * 14}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleFolder(entry.key, isOpen)}
              aria-expanded={isOpen}
              aria-label={entry.label}
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-[var(--fg-faint)] hover:text-[var(--fg-default)]"
            >
              <ChevronRight
                className={`h-3 w-3 transition-transform duration-150 ${
                  isOpen ? "rotate-90" : onRight ? "rotate-180" : ""
                }`}
              />
            </button>
          ) : (
            <span className="w-4 shrink-0" aria-hidden />
          )}
          <Link
            href={entry.href}
            onClick={() => onNavigate(entry.key)}
            data-cc-id="sidebar.classic.link"
            data-active={entry.isActive || undefined}
            className="flex min-w-0 flex-1 items-center gap-2"
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{entry.label}</span>
          </Link>
          <span
            className={`flex shrink-0 items-center gap-0.5 transition-opacity ${
              entry.isFav ? "opacity-100" : "opacity-0 group-hover/row:opacity-100 focus-within:opacity-100"
            }`}
          >
            <button
              type="button"
              onClick={() => onToggleFav(entry.href, entry.label)}
              aria-pressed={entry.isFav}
              aria-label={TEXT.fav[lang]}
              title={TEXT.fav[lang]}
              className={`rounded p-0.5 hover:bg-[var(--bg-chrome)] ${
                entry.isFav ? "text-[var(--cc-accent-300)]" : "text-[var(--fg-faint)]"
              }`}
            >
              <Star className="h-3 w-3" fill={entry.isFav ? "currentColor" : "none"} />
            </button>
            <button
              type="button"
              onClick={(e) => onMore(e, entry)}
              aria-label={TEXT.more[lang]}
              title={TEXT.more[lang]}
              className="rounded p-0.5 text-[var(--fg-faint)] opacity-0 hover:bg-[var(--bg-chrome)] hover:text-[var(--fg-default)] group-hover/row:opacity-100"
            >
              <MoreHorizontal className="h-3 w-3" />
            </button>
          </span>
        </div>
        {isOpen && (
          <ul className="mt-px space-y-px">{entry.children!.map((c) => renderRow(c, depth + 1))}</ul>
        )}
      </li>
    );
  };

  const visibleSections = sections.filter((s) => s.entries.length > 0);

  return (
    <div data-cc-id="sidebar.classic" className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 px-2 pt-2 pb-1">
        <label className="flex h-7 items-center gap-2 rounded-md px-1.5 text-[0.8125rem] text-[var(--fg-muted)] focus-within:bg-[var(--bg-muted)] hover:bg-[var(--bg-muted)]">
          <Search className="h-4 w-4 shrink-0" />
          <input
            // type="search", not "text": a global !important rule boxes every
            // text input, and here the row itself is the field.
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={TEXT.search[lang]}
            className="min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-[var(--fg-default)] shadow-none placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-0 [&::-webkit-search-cancel-button]:hidden"
          />
          {searching && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              aria-label="Clear"
              className="rounded p-0.5 text-[var(--fg-faint)] hover:text-[var(--fg-default)]"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </label>
      </div>

      <nav aria-label={TEXT.spaces[lang]} className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {favorites.length > 0 && !searching && (
          <section className="pt-2">
            <h3 className="px-1.5 pb-1 text-[0.6875rem] font-medium text-[var(--fg-faint)]">
              {TEXT.favorites[lang]}
            </h3>
            <ul className="space-y-px">{favorites.map((f) => renderRow({ ...f, key: `fav:${f.key}`, children: undefined }, 0))}</ul>
          </section>
        )}

        <div className="pt-3">
          {!searching && (
            <h3 className="px-1.5 pb-1 text-[0.6875rem] font-medium text-[var(--fg-faint)]">
              {TEXT.spaces[lang]}
            </h3>
          )}
          {visibleSections.length === 0 && (
            <p className="px-1.5 py-2 text-[0.75rem] text-[var(--fg-faint)]">{TEXT.empty[lang]}</p>
          )}
          <ul className="space-y-px">
            {visibleSections.map((section) => {
              const open = searching || !section.collapsed;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => onToggleSection(section.id)}
                    aria-expanded={open}
                    className="group/space flex h-7 w-full items-center gap-1.5 rounded-md px-1.5 text-[0.8125rem] font-medium text-[var(--fg-default)] hover:bg-[var(--bg-muted)]"
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-[var(--cc-accent-600-20)] text-[0.625rem] leading-none text-[var(--cc-accent-300)]">
                      {section.emoji || section.label.charAt(0)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-start">{section.label}</span>
                    <ChevronRight
                      className={`h-3 w-3 shrink-0 text-[var(--fg-faint)] opacity-0 transition-all duration-150 group-hover/space:opacity-100 ${
                        open ? "rotate-90" : onRight ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {open && <ul className="mt-px space-y-px">{section.entries.map((e) => renderRow(e, 1))}</ul>}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {footer && <div className="shrink-0 border-t border-[var(--border-hairline)] px-2 py-1.5">{footer}</div>}
    </div>
  );
}
